import isFunction from 'lodash/isFunction';
import { CaretDown } from '@phosphor-icons/react';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import React from 'react';
import ResetButton from './ResetButton';

const headerWrapperStyles = css`
  border-top: 1px dotted ${etiColors.grayScale[9]};
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  padding: 15px 0;

  @media ${mediaQueries.medium.up} {
    border-top: none;
    flex-wrap: wrap;
    padding: 15px 0 0;
  }
`;

const clickable = css`
  cursor: pointer;
`;

const headerStyles = css`
  color: ${etiColors.black};
  display: flex;
  flex-basis: 70%;
  font-size: 1rem;
  font-weight: 700;
  line-height: 19px;
`;

const controlsStyles = css`
  color: ${etiColors.grayScale[10]};
  display: flex;
`;

const controlButtonStyles = css`
  align-items: center;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  margin: 0;
  outline: inherit;
  padding: 0;
`;

const resetButtonPlaceholderStyles = css`
  @media ${mediaQueries.medium.up} {
    flex: 1 1 100%;
    height: 26px;
  }
`;

const iconStyles = (isOpen: boolean) => css`
  transform: rotate(${isOpen ? '180deg' : '0deg'});
`;

const separatorStyles = css`
  display: inline-block;
  padding: 0 3px;
`;

interface FilterHeaderProps {
  destinationName: string;
  isOpen: boolean;
  isSelected: boolean;
  isToggleable: boolean;
  label: string;
  name: string;
  onClick: () => void;
  onReset: () => void;
  originName: string;
  shouldShowResetButton: boolean;
}

const FilterHeader = ({
  destinationName,
  isOpen,
  isSelected,
  isToggleable = true,
  label,
  name,
  onClick,
  onReset,
  originName,
  shouldShowResetButton,
}: FilterHeaderProps) => {
  const handleReset = (e: React.MouseEvent) => {
    if (isFunction(onReset)) {
      onReset();
      e.stopPropagation();
    }
  };

  return (
    <header
      className={cx(headerWrapperStyles, `header ${isToggleable ? clickable : ''}`)}
      data-testid={`resultPage-${name}-header`}
      {...(isToggleable && { onClick })}
    >
      <div className={headerStyles}>
        {label || (
          <>
            <span>{originName}</span>
            <span className={separatorStyles}>–</span>
            <span>{destinationName}</span>
          </>
        )}
      </div>
      {isToggleable && (
        <div className={cx(controlsStyles, 'controls')}>
          <button
            className={controlButtonStyles}
            data-testid={`resultPage-filterHeader-${name}ToggleButton-button`}
            type="button"
          >
            <CaretDown aria-hidden className={iconStyles(isOpen)} size={22} weight="light" />
          </button>
        </div>
      )}
      {shouldShowResetButton &&
        (isSelected ? (
          <ResetButton
            dataTestId={`resultPage-filterHeader-${name}FilterResetButton-button`}
            onClick={handleReset}
          />
        ) : (
          <div className={resetButtonPlaceholderStyles} />
        ))}
    </header>
  );
};

export default FilterHeader;
